import {
  useState,
  useEffect,
  useContext,
  createContext,
  FunctionComponent,
  PropsWithoutRef,
  CSSProperties,
  Ref,
  forwardRef
} from 'react';
import {
  NimblePicker,
  CategoryName,
  Data as EmojiData,
  BackgroundImageFn,
  PickerProps
} from 'emoji-mart';

import { useTheme } from '../../hooks';
import { BaseProps, NoChildrenProp } from '../../types';

import StyledEmojiPicker from './EmojiPicker.styles';

type EmojiSet = 'apple' | 'google' | 'twitter' | 'facebook';

const emojiDataCache = new Map<EmojiSet | 'all', EmojiData>();

export interface EmojiPickerProps extends BaseProps, NoChildrenProp {
  /** Individual emojis size in pixels. */
  emojiSize?: number;
  /** Number of emoji per line in a picker. */
  perLine?: number;
  /** Don't load excluded categories. Accepts [I18n categories keys](https://www.npmjs.com/package/emoji-mart#i18n). */
  exclude?: CategoryName[];
  /** Show footer for picker. */
  showFooter?: boolean;
  /** A Fn that gets called on selecting an emoji from picker. */
  onSelect?: PickerProps['onSelect'];
  /** Ref for the emoji Picker */
  ref?: Ref<HTMLDivElement>;
}

export interface EmojiContextValue {
  /**
   * A Fn that returns that image sheet to use for emojis.
   * If no sprite is provided, native emoji are used.
   * For the source of image please visit this [link](https://www.jsdelivr.com/package/npm/emoji-datasource?version=5.0.1&path=img)
   */
  spriteSrcResolver?: BackgroundImageFn;
  /** Emoji set to be used. */
  set?: EmojiSet;
  /** Data to be used. [link](https://github.com/iamcal/emoji-data).
   * Use this in case you would like to filter out some emoji,  one must change the sprite image accordingly. */
  overrideData?: EmojiData;
}

export const EmojiContext = createContext<EmojiContextValue>({});
const EmojiPicker: FunctionComponent<EmojiPickerProps> = forwardRef(
  (
    {
      emojiSize = 24,
      perLine = 9,
      showFooter = true,
      exclude,
      onSelect,
      ...restProps
    }: PropsWithoutRef<EmojiPickerProps>,
    ref: Ref<HTMLDivElement>
  ) => {
    const theme = useTheme();
    const { set, spriteSrcResolver, overrideData } = useContext(EmojiContext);
    const [data, setData] = useState<EmojiData | null>(null);

    useEffect(() => {
      const dataSet = set ?? 'all';

      // Unmount NimblePicker for changes to take effect
      setData(null);

      if (overrideData) {
        setTimeout(() => {
          setData(overrideData);
        }, 0);
      } else if (emojiDataCache.has(dataSet)) {
        setTimeout(() => {
          setData(emojiDataCache.get(dataSet) ?? null);
        }, 0);
      } else {
        import(`emoji-mart/data/${dataSet}.json`)
          .then(({ default: json }) => {
            emojiDataCache.set(dataSet, json);
            setData(emojiDataCache.get(dataSet)!);
          })
          .catch(() => {
            setData(null);
          });
      }
    }, [set, overrideData]);

    return (
      data && (
        <StyledEmojiPicker
          ref={ref}
          showFooter={showFooter}
          style={{ '--emoji-size': `${emojiSize}px` } as CSSProperties}
        >
          <NimblePicker
            {...restProps}
            set={set}
            native={!spriteSrcResolver}
            backgroundImageFn={spriteSrcResolver}
            data={data}
            emojiSize={emojiSize}
            perLine={perLine}
            exclude={exclude}
            onSelect={onSelect}
            color={theme.base.palette.interactive}
            emoji=''
            title=''
            emojiTooltip
            showSkinTones={showFooter}
            showPreview={false}
          />
        </StyledEmojiPicker>
      )
    );
  }
);

export default EmojiPicker;
